import React, { useState, useEffect } from 'react';
import { FiSave, FiX, FiImage, FiPlus, FiTrash2 } from 'react-icons/fi';
import { useAuthStore } from '../store/useAuthStore';
import '../styles/SimplifiedPageEditor.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function SimplifiedPageEditor({ pageId, onClose, onSaveComplete }) {
  const token = useAuthStore((state) => state.token);

  const [title, setTitle] = useState('');
  const [heading, setHeading] = useState('');
  const [headingFontSize, setHeadingFontSize] = useState('3.5');
  const [paragraphs, setParagraphs] = useState([]);
  const [paragraphFontSize, setParagraphFontSize] = useState('1.1');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [newParagraph, setNewParagraph] = useState('');

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [showImageUpload, setShowImageUpload] = useState(false);

  // Load existing page data when editing
  useEffect(() => {
    if (pageId) {
      const fetchPageData = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/pages/${pageId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (response.ok) {
            const page = await response.json();
            setTitle(page.title || '');
            // Parse the HTML content to extract heading, paragraphs, image, and font sizes
            const content = page.content || '';

            // Extract heading font size from style attribute
            const headingSizeMatch = content.match(/font-size:\s*([\d.]+)rem/);
            if (headingSizeMatch) {
              setHeadingFontSize(headingSizeMatch[1]);
            }

            // Extract paragraph font size - look for the font-size in the div containing paragraphs
            const paragraphSizeMatch = content.match(/font-size:\s*([\d.]+)rem;[\s\S]*?>/);
            if (paragraphSizeMatch) {
              setParagraphFontSize(paragraphSizeMatch[1]);
            }

            // Extract heading text
            const headingMatch = content.match(/<h1[^>]*>([^<]+)<\/h1>/);
            if (headingMatch) {
              setHeading(headingMatch[1]);
            }

            // Extract paragraphs
            const paragraphMatches = [...content.matchAll(/<p[^>]*>([^<]+)<\/p>/g)];
            const extractedParagraphs = paragraphMatches.map(match => match[1]);
            setParagraphs(extractedParagraphs);
          }
        } catch (error) {
          console.error('Error loading page data:', error);
          setMessage('Error loading page data');
        }
      };
      fetchPageData();
    }
  }, [pageId, token]);

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', files[0]);

      const response = await fetch(`${API_BASE_URL}/api/pages/${pageId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setBackgroundImageUrl(data.fileUrl);
        setMessage('Image uploaded successfully!');
        setShowImageUpload(false);
        setTimeout(() => setMessage(''), 3000);
      } else {
        try {
          const errorData = await response.json();
          setMessage(`Error: ${errorData.message || 'Failed to upload image'}`);
        } catch {
          setMessage(`Error uploading image (HTTP ${response.status})`);
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage('Error uploading image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddParagraph = () => {
    if (newParagraph.trim()) {
      setParagraphs([...paragraphs, newParagraph.trim()]);
      setNewParagraph('');
    }
  };

  const handleRemoveParagraph = (index) => {
    setParagraphs(paragraphs.filter((_, i) => i !== index));
  };

  const handleParagraphChange = (index, value) => {
    const updated = [...paragraphs];
    updated[index] = value;
    setParagraphs(updated);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Build HTML content - centered heading with paragraphs only
let htmlContent = `
<div style="
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url('${backgroundImageUrl}');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
  text-align: center;
  padding: 2rem;
">
  <div style="
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 900px;
  ">
    <h1 style="
      font-size: ${headingFontSize}rem;
      margin-bottom: 2rem;
      font-family: 'Saira Condensed', sans-serif;
      font-weight: 700;
    ">${heading}</h1>

    <div style="
      font-size: ${paragraphFontSize}rem;
      font-family: 'Saira Condensed', sans-serif;
      line-height: 1.8;
      text-align: center;
      max-width: 800px;
    ">
      ${paragraphs.map(p => `<p style="margin: 1.2rem 0;">${p}</p>`).join('')}
    </div>
  </div>
</div>`;

      const response = await fetch(`${API_BASE_URL}/api/pages/${pageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          content: htmlContent,
        }),
      });

      if (response.ok) {
        setMessage(pageId && paragraphs.length > 0 ? 'Page updated successfully!' : 'Page created successfully!');
        setTimeout(() => {
          setMessage('');
          onSaveComplete && onSaveComplete();
        }, 2000);
      } else {
        const errorData = await response.json();
        setMessage(`Error: ${errorData.message || 'Failed to save'}`);
      }
    } catch (error) {
      console.error('Error saving page:', error);
      setMessage('Error saving page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="simplified-editor-overlay">
      <div className="simplified-editor-container">
        {/* Header */}
        <div className="simplified-editor-header" style={{ position: 'relative' }}>
          <h1>Create Page (Simplified)</h1>
          <button className="btn-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        {/* Messages */}
        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSave} className="simplified-editor-form">
          {/* Page Title */}
          <div className="form-section">
            <h3>Page Title</h3>
            <div className="form-group">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter page title (for navigation)"
                className="form-input"
                required
              />
            </div>
          </div>

          {/* Heading */}
          <div className="form-section">
            <h3>Page Heading</h3>
            <div className="form-group">
              <input
                type="text"
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                placeholder="Enter main heading displayed on page"
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="heading-font-size">Heading Font Size:</label>
              <div className="font-size-input-group">
                <input
                  id="heading-font-size"
                  type="range"
                  min="1"
                  max="5"
                  step="0.5"
                  value={headingFontSize}
                  onChange={(e) => setHeadingFontSize(e.target.value)}
                  className="range-slider"
                />
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.5"
                  value={headingFontSize}
                  onChange={(e) => setHeadingFontSize(e.target.value)}
                  className="font-size-number"
                />
                <span className="font-size-unit">rem</span>
              </div>
              <small>Preview: <span style={{ fontSize: `${headingFontSize}rem`, fontFamily: "'Saira Condensed', sans-serif" }}>Sample Heading</span></small>
            </div>
          </div>

          {/* Background Image */}
          <div className="form-section">
            <div className="section-header">
              <h3>Background Image (Optional)</h3>
              <button
                type="button"
                className="btn-upload-image"
                onClick={() => setShowImageUpload(!showImageUpload)}
              >
                <FiImage /> Upload Image
              </button>
            </div>

            {showImageUpload && (
              <div className="upload-section">
                <label htmlFor="image-upload" className="upload-label">
                  <FiPlus size={20} />
                  Click to upload image
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="file-input"
                />
                <small>Max file size: 50MB</small>
              </div>
            )}

            {backgroundImageUrl && (
              <div className="image-preview">
                <img src={backgroundImageUrl} alt="Background preview" />
                <p>Current background image</p>
              </div>
            )}
          </div>

          {/* Paragraphs */}
          <div className="form-section">
            <h3>Content Paragraphs</h3>

            <div className="form-group">
              <label htmlFor="paragraph-font-size">Paragraph Font Size:</label>
              <div className="font-size-input-group">
                <input
                  id="paragraph-font-size"
                  type="range"
                  min="0.75"
                  max="2"
                  step="0.1"
                  value={paragraphFontSize}
                  onChange={(e) => setParagraphFontSize(e.target.value)}
                  className="range-slider"
                />
                <input
                  type="number"
                  min="0.75"
                  max="2"
                  step="0.1"
                  value={paragraphFontSize}
                  onChange={(e) => setParagraphFontSize(e.target.value)}
                  className="font-size-number"
                />
                <span className="font-size-unit">rem</span>
              </div>
              <small>Preview: <span style={{ fontSize: `${paragraphFontSize}rem`, fontFamily: "'Saira Condensed', sans-serif" }}>Sample paragraph text</span></small>
            </div>

            {paragraphs.length > 0 && (
              <div className="paragraphs-list">
                {paragraphs.map((para, index) => (
                  <div key={index} className="paragraph-item">
                    <div className="paragraph-number">{index + 1}</div>
                    <textarea
                      value={para}
                      onChange={(e) => handleParagraphChange(index, e.target.value)}
                      className="paragraph-textarea"
                      rows="3"
                    />
                    <button
                      type="button"
                      className="btn-remove-paragraph"
                      onClick={() => handleRemoveParagraph(index)}
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="add-paragraph-section">
              <textarea
                value={newParagraph}
                onChange={(e) => setNewParagraph(e.target.value)}
                placeholder="Enter a new paragraph..."
                className="new-paragraph-textarea"
                rows="3"
              />
              <button
                type="button"
                className="btn-add-paragraph"
                onClick={handleAddParagraph}
                disabled={!newParagraph.trim()}
              >
                Add Paragraph
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="simplified-editor-actions">
            <button type="submit" className="btn-save" disabled={saving || !title || !heading}>
              <FiSave size={18} />
              {saving ? 'Creating...' : 'Create Page'}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
